import React, { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PullToRefresh = ({ 
  onRefresh, 
  children, 
  disabled = false,
  threshold = 80,
  maxPull = 120,
  refreshingText = "Refreshing...",
  pullText = "Pull to refresh",
  releaseText = "Release to refresh"
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef(null);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    // Only enable pull-to-refresh when at top of scroll
    const container = containerRef.current;
    if ((container && container.scrollTop > 5) || window.scrollY > 5) return;
    
    startYRef.current = e.touches[0].clientY;
    currentYRef.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const container = containerRef.current;
    if ((container && container.scrollTop > 5) || window.scrollY > 5) {
      setPullDistance(0);
      return;
    }
    
    currentYRef.current = e.touches[0].clientY;
    const diff = currentYRef.current - startYRef.current;
    
    if (diff > 0) {
      // Apply resistance to the pull
      const resistance = 0.5;
      const distance = Math.min(diff * resistance, maxPull);
      setPullDistance(distance);
      
      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, disabled, isRefreshing, maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(60); // Keep some distance while refreshing
      
      try {
        await onRefresh?.();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, disabled, pullDistance, threshold, isRefreshing, onRefresh]);

  const progress = Math.min(pullDistance / threshold, 1);
  const shouldRelease = pullDistance >= threshold;

  return (
    <div 
      ref={containerRef}
      className="relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: pullDistance > 10 ? 'none' : 'auto' }}
    >
      {/* Pull indicator */}
      <AnimatePresence>
        {(pullDistance > 0 || isRefreshing) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-0 right-0 flex flex-col items-center justify-center z-50 pointer-events-none"
            style={{ 
              top: 0,
              height: Math.max(pullDistance, isRefreshing ? 60 : 0),
              background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0.95), rgba(26, 26, 26, 0))'
            }}
          >
            <motion.div
              animate={{ 
                rotate: isRefreshing ? 360 : progress * 180,
                scale: isRefreshing ? 1 : 0.8 + progress * 0.2
              }}
              transition={{ 
                rotate: isRefreshing ? { duration: 1, repeat: Infinity, ease: 'linear' } : { duration: 0 }
              }}
              className={`p-2 rounded-full ${
                shouldRelease || isRefreshing 
                  ? 'bg-wt-gold/20 text-wt-gold' 
                  : 'bg-wt-subtle text-wt-text-muted'
              }`}
            >
              <RefreshCw className="w-5 h-5" />
            </motion.div>
            <motion.span 
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: progress > 0.3 ? 1 : 0, y: 0 }}
              className="text-xs mt-1 text-wt-text-muted"
            >
              {isRefreshing ? refreshingText : shouldRelease ? releaseText : pullText}
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with transform */}
      <div 
        style={{ 
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
